/**
 * Reads the project's map (`$map.csv`) for the variables that can hold a
 * respondent ID, the way the engine reads a map: the LABEL, TYPE and VARIABLE
 * columns are found by their header names, in any order, and the header row
 * decides whether fields are separated by commas or tabs.
 */

/** The byte order mark a map saved from Excel starts with. */
const BOM = String.fromCharCode(0xfeff)

/** The map types whose column can take the numbers 1 to N. */
const ID_TYPES = new Set(["INT", "TEXT"])

/** The header names the engine requires to read a map's rows. */
const LABEL = "LABEL"
const TYPE = "TYPE"
const VARIABLE = "VARIABLE"

/**
 * The field separator of the map: a tab when its first line holds more tabs
 * than commas (a tab-delimited data file usually comes with a tab-delimited
 * map), otherwise a comma.
 */
const separatorOf = (text: string): string => {
  const end = text.search(/\r|\n/)
  const header = end === -1 ? text : text.slice(0, end)
  const tabs = header.split("\t").length - 1
  const commas = header.split(",").length - 1
  return tabs > commas ? "\t" : ","
}

/**
 * The text's records, each a list of trimmed cells. A quoted cell may hold
 * the separator, doubled quotes and line breaks. Blank lines are dropped.
 */
const recordsOf = (text: string, sep: string): string[][] => {
  const records: string[][] = []
  let record: string[] = []
  let cell = ""
  let inQuotes = false
  const endCell = (): void => {
    record.push(cell.trim())
    cell = ""
  }
  const endRecord = (): void => {
    endCell()
    if (record.some((c) => c !== "")) records.push(record)
    record = []
  }
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"'
        i++
      } else if (ch === '"') inQuotes = false
      else cell += ch
    } else if (ch === '"') inQuotes = true
    else if (ch === sep) endCell()
    else if (ch === "\r" || ch === "\n") {
      if (ch === "\r" && text[i + 1] === "\n") i++
      endRecord()
    } else cell += ch
  }
  endRecord()
  return records
}

/**
 * The VARIABLE names of the map's INT and TEXT rows, in map order, each
 * once. Empty when the map's header has no LABEL, TYPE or VARIABLE column (the
 * engine cannot read such a map either); rows without a variable name are
 * skipped.
 */
export const mapIdVariables = (mapText: string): string[] => {
  const text = mapText.startsWith(BOM) ? mapText.slice(1) : mapText
  const [header, ...rows] = recordsOf(text, separatorOf(text))
  if (!header) return []
  const typeAt = header.indexOf(TYPE)
  const variableAt = header.indexOf(VARIABLE)
  if (!header.includes(LABEL) || typeAt === -1 || variableAt === -1) return []
  const seen = new Set<string>()
  rows.forEach((row) => {
    const name = row[variableAt] ?? ""
    if (name && ID_TYPES.has((row[typeAt] ?? "").toUpperCase())) seen.add(name)
  })
  return [...seen]
}
